import { useState } from "react";
import { useThemeContext } from "../../theme/ThemeContext";
import { RADIUS } from "../../theme/tokens";
import { useLockBodyScroll } from "../../hooks/useLockBodyScroll";
import { isValidShift, parseShift, calcSection, formatShift } from "../../utils/timeUtils";
import { exportCSV } from "../../utils/exportUtils";
import { StarMark, Button } from "../shared";
import { overlayBackdrop } from "../../theme/styles";

function ExportShiftModal({ entries, dateLabel, saLogoData, activeSCCO, isAdmin, SA, GLASS, onClose }) {
  const { TYPOGRAPHY } = useThemeContext();
  useLockBodyScroll();
  const [startTime, setStartTime] = useState("");
  const [endTime,   setEndTime]   = useState("");
  const [error,     setError]     = useState("");

  const combined = startTime + "-" + endTime;
  const valid = isValidShift(combined);

  const formatTimeInput = val => val.replace(/[^0-9]/g, "").slice(0,4);

  const handleExport = (format) => {
    if (!valid) { setError("Enter valid 24hr times, e.g. 0930 and 1600"); return; }
    const parsed = parseShift(combined);
    const tagged = entries.map(e => ({ ...e, section: calcSection(e, parsed) }));
    exportCSV(tagged, dateLabel, saLogoData, activeSCCO, combined, format);
    onClose();
  };

  const timeInputStyle = val => ({
    width: "100%", boxSizing: "border-box",
    background: SA.darkGray, borderRadius: RADIUS.sm, color: SA.white,
    padding: "12px 0", fontSize: 28, letterSpacing: "0.12em", textAlign: "center",
    fontFamily: "'HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif", outline: "none",
    border: `1px solid ${val.length === 4 ? "#4a7a4a" : SA.borderGray}`,
  });

  return (
    <div onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }} style={{ ...overlayBackdrop, zIndex: 400,
      display: "flex", alignItems: "center", justifyContent: "center", padding: 24,
      fontFamily: "'HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif" }}>
      <div onClick={e => e.stopPropagation()} style={{ ...GLASS.modal,
        width: "100%", maxWidth: 380,
        borderRadius: 16, padding: "28px 24px" }}>

        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <StarMark size={14} color={SA.lightSilver} />
            <span style={{ fontSize: TYPOGRAPHY.caption, color: SA.lightSilver, letterSpacing: "0.2em",
              textTransform: "uppercase", fontWeight: 600 }}>Export Shift Report</span>
          </div>
          <Button variant="icon" onClick={onClose}>×</Button>
        </div>

        <p style={{ fontSize: TYPOGRAPHY.caption, color: SA.silver, lineHeight: 1.7, marginBottom: 24 }}>
          Enter your shift window. Connections with arrivals or departures after your go-home time will be marked <strong style={{color:SA.lightSilver}}>POST-SHIFT</strong> in the report.
        </p>

        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: TYPOGRAPHY.label, color: SA.silver, marginBottom: 6, fontWeight: 500 }}>Shift Start</div>
            <input value={startTime} onChange={e => { setError(""); setStartTime(formatTimeInput(e.target.value)); }}
              placeholder="0930" maxLength={4} inputMode="numeric" style={timeInputStyle(startTime)} />
          </div>
          <div style={{ fontSize: 22, color: SA.borderGray, paddingTop: 20, fontFamily: "'HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif" }}>–</div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: TYPOGRAPHY.label, color: SA.silver, marginBottom: 6, fontWeight: 500 }}>Shift End</div>
            <input value={endTime} onChange={e => { setError(""); setEndTime(formatTimeInput(e.target.value)); }}
              placeholder="1600" maxLength={4} inputMode="numeric" style={timeInputStyle(endTime)} />
          </div>
        </div>

        {error && <div style={{ fontSize: TYPOGRAPHY.label, color: "#c06060", marginBottom: 8, letterSpacing: "0.06em" }}>{error}</div>}
        {valid && !error && (
          <div style={{ fontSize: TYPOGRAPHY.label, color: "#8fbf8f", marginBottom: 8, letterSpacing: "0.08em" }}>{formatShift(combined)}</div>
        )}

        {activeSCCO && (
          <div style={{ marginTop: 12, padding: "10px 14px", background: "rgba(74,124,247,0.08)",
            border: `1px solid rgba(74,124,247,0.25)`, borderRadius: RADIUS.sm,
            fontSize: TYPOGRAPHY.body, color: SA.lightSilver, display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ color: SA.silver, fontSize: TYPOGRAPHY.caption, letterSpacing: "0.06em", textTransform: "uppercase" }}>Prepared by</span>
            <span style={{ color: SA.white, fontWeight: 700, letterSpacing: "0.02em" }}>{activeSCCO.name}</span>
            {activeSCCO.badge && (
              <span style={{ color: SA.accent, fontFamily: "'HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif",
                fontSize: TYPOGRAPHY.caption, fontWeight: 600 }}>#{activeSCCO.badge}</span>
            )}
          </div>
        )}

        <div style={{ height: 16 }} />

        <div style={{ display: "flex", gap: 8 }}>
          {isAdmin && (
            <Button variant="primary" size="lg" fullWidth disabled={!valid}
              onClick={() => handleExport("html")}
              style={{ flex: 1, gap: 8 }}>
              ↓ HTML
            </Button>
          )}
          <Button variant="primary" size="lg" fullWidth disabled={!valid}
            onClick={() => handleExport("pdf")}
            style={{ flex: 1, gap: 8,
              background: isAdmin ? "rgba(74,124,247,0.15)" : undefined,
              color: isAdmin ? SA.accent : undefined }}>
            ⎙ PDF
          </Button>
        </div>
        <Button variant="secondary" size="lg" fullWidth onClick={onClose} style={{ marginTop: 10 }}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

export { ExportShiftModal };
